import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { usePreviousValue } from 'hooks/usePrevious';

import { useEffect, useMemo } from 'react';

import { bimWorkOrderObjectsForListGql, bimWorkOrderObjectsGql } from '../bimWorkOrderQueries';

interface IUseBimWorkOrderObjects {
    workOrderId?: number;
    rootsOnly?: boolean;
    withEffect?: boolean;
    requestKey?: string;
    rootId?: number;
    getKeyValuesOnly?: boolean;
}

export const useBimWorkOrderObjects = (props: IUseBimWorkOrderObjects) => {
    const { workOrderId, rootId, rootsOnly, withEffect = true, requestKey, getKeyValuesOnly } = props;

    const prevWorkOrderId = usePreviousValue(workOrderId);

    const requestId = useMemo(
        () => `bimWorkOrderObjects${getKeyValuesOnly ? 'ForList' : ''}${requestKey}`,
        [getKeyValuesOnly, requestKey],
    );

    const where = useMemo(() => (rootId ? { rootObjectId: { eq: rootId } } : undefined), [rootId]);

    const variables = useMemo(
        () => ({
            workOrderId,
            rootsOnly,
            where,
        }),
        [rootsOnly, where, workOrderId],
    );

    const [reset] = useResetStoreData({ requestId });

    useEffect(
        () => () => {
            reset(undefined);
        },
        [reset],
    );

    useEffect(() => {
        if (prevWorkOrderId && prevWorkOrderId && workOrderId !== prevWorkOrderId) {
            reset(undefined);
        }
    }, [prevWorkOrderId, reset, workOrderId]);

    const [bimWorkOrderObjectsData, bimWorkOrderObjectsStatus, bimWorkOrderObjectsError] = useRequest({
        requestId,
        query: bimWorkOrderObjectsGql,
        withEffect: withEffect && !getKeyValuesOnly && Boolean(workOrderId),
        variables,
    });

    const [bimWorkOrderObjectsForListData, bimWorkOrderObjectsForListStatus, bimWorkOrderObjectsForListError] =
        useRequest({
            requestId,
            query: bimWorkOrderObjectsForListGql,
            withEffect: withEffect && getKeyValuesOnly && Boolean(workOrderId),
            variables,
        });

    const data = useMemo(
        () =>
            getKeyValuesOnly
                ? bimWorkOrderObjectsForListData?.bimWorkOrderObjects
                : bimWorkOrderObjectsData?.bimWorkOrderObjects,
        [
            bimWorkOrderObjectsData?.bimWorkOrderObjects,
            bimWorkOrderObjectsForListData?.bimWorkOrderObjects,
            getKeyValuesOnly,
        ],
    );

    const status = useMemo(
        () => (getKeyValuesOnly ? bimWorkOrderObjectsForListStatus : bimWorkOrderObjectsStatus),
        [bimWorkOrderObjectsForListStatus, bimWorkOrderObjectsStatus, getKeyValuesOnly],
    );

    const error = useMemo(
        () => (getKeyValuesOnly ? bimWorkOrderObjectsForListError : bimWorkOrderObjectsError),
        [bimWorkOrderObjectsError, bimWorkOrderObjectsForListError, getKeyValuesOnly],
    );

    return {
        reset,
        data,
        status,
        error,
    };
};
